import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { registerRewardBehavior } from 'services/actionlog.service';
import { getPlayerInfo, getServerTime } from 'services/dashboard.service';
import { User } from 'types/user';

import { BehaviorKey, LEADER_ONLY_BEHAVIOR, Recognition, RecognitionAlertContent } from './recognition';
import { logRecognitionAction } from './recognition-log';
import {
  FREE_TEXT_OPTION,
  MessageContent,
  MessageOption,
  coinAllowsPresets,
  contentFromHtml,
  isMessageValid,
  messageOptionFor,
  resolveMessage,
} from './recognition-message';
import { cycleIsOpenAt, warnNoOpenPhase } from './recognition-phase';
import { CoinKey, optionState } from './recognition-points';
import { STEP, Step, canAdvance } from './recognition-steps';
import { useColleagueChecks } from './use-colleague-checks';
import { useColleagueSearch } from './use-colleague-search';
import { RecognitionDraft, notifyDraftChanged, readRecognitionDraft, useRecognitionDraft } from './use-recognition-draft';

const SENT_MESSAGE =
  'Você reconheceu um colega. Agradecemos por ajudar a tornar o BB um ambiente colaborativo e agradável para trabalhar.';
const FAILED_MESSAGE = 'Ocorreu um erro com a sua solicitação. Por favor, tente novamente em alguns minutos.';

/** Recusas do servidor que nova tentativa não resolve: a tela de erro não oferece "Tentar novamente". */
const FINAL_REFUSALS = [
  'Não existe fase aberta para envio de reconhecimento.',
  'Você não possui saldo suficiente para reconhecer. Por favor, aguarde até a próxima distribuição.',
  'Você já enviou o limite máximo de 300 pontos, para a mesma pessoa, neste mês.',
  'Você não pode emitir reconhecimentos. Em caso de dúvidas, entre em contato com nossa equipe.',
  'Você já fez esse reconhecimento ao mesmo usuário nesta fase.',
];

/**
 * Resposta do envio. Guarda o reconhecimento como foi mandado: a tela de resultado mostra o que
 * saiu, e não as escolhas da tela, que seguem sujeitas às regras das etapas.
 */
export type RecognitionResult = { sent: boolean; serverMessage: string; recognition: Recognition };

export type RecognitionView = 'steps' | 'sending' | 'sent' | 'failed';

/**
 * Estado e regras do reconhecimento: as escolhas de cada etapa, o rascunho e o envio. A busca e
 * as conferências do colega ficam nos hooks próprios; a página só desenha o que este devolve.
 */
export function useRecognitionFlow() {
  const navigate = useNavigate();
  const { id: colleagueIdFromRoute } = useParams();
  const { user, refreshUser, currentCycle } = useAuth();

  const [step, setStep] = useState<Step>(STEP.colleague);
  const [target, setTarget] = useState<User>();
  const [behavior, setBehavior] = useState<BehaviorKey>();
  const [coin, setCoin] = useState<CoinKey>();
  const [points, setPoints] = useState<number>();
  const [messageOption, setMessageOption] = useState<MessageOption>();
  const [freeText, setFreeText] = useState<MessageContent>();

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<RecognitionResult>();

  const [playerInfo, setPlayerInfo] = useState<any>({});
  const [alert, setAlert] = useState<RecognitionAlertContent>();

  const [loadingDraft, setLoadingDraft] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(false);

  // A matrícula da rota só abre a busca: depois que alguém foi escolhido, a busca volta com o campo vazio (AJ3-41).
  const [routeTermUsed, setRouteTermUsed] = useState(false);

  const balance = user?.point_categories?.point_behavior || 0;
  const message = resolveMessage(messageOption, freeText, coin);
  const messageHtml = message?.html;
  const messageIsValid = isMessageValid(message);

  /** As escolhas completas: o que a revisão mostra e o envio manda. */
  const recognition: Recognition | undefined =
    target && behavior && coin && points !== undefined && messageHtml
      ? { target, behavior, coin, points, message: messageHtml }
      : undefined;

  const stepIsComplete = canAdvance(step, {
    hasTarget: !!target,
    hasBehavior: !!behavior,
    hasCoin: !!coin,
    hasPoints: points !== undefined,
    messageIsValid,
  });

  /** Tira o colega e volta à busca, mantendo as outras escolhas para o próximo. */
  const releaseTarget = useCallback(() => {
    setTarget(undefined);
    setStep(STEP.colleague);
  }, []);

  /** O colega escolhido não pode ser reconhecido: volta à busca e explica o motivo. */
  const refuseTarget = useCallback(
    (reason: RecognitionAlertContent) => {
      releaseTarget();
      setAlert(reason);
    },
    [releaseTarget],
  );

  const returnToMessage = useCallback((reason: RecognitionAlertContent) => {
    setAlert(reason);
    setStep(STEP.message);
  }, []);

  const { search, searchColleague: findColleagues, clearSearch, cancelAutoPick } = useColleagueSearch(user?._id);
  const { saveSoon: saveDraftSoon, clear: clearSavedDraft } = useRecognitionDraft(user?._id);
  const { pointsSent, sentPointsNotice, askingRelationship, closeRelationshipQuestion } = useColleagueChecks({
    target,
    sender: playerInfo,
    userId: user?._id,
    messageInReview: step === STEP.review ? messageHtml : undefined,
    onRefused: refuseTarget,
    onMessageAlreadySent: returnToMessage,
  });

  function logAction(action: Record<string, unknown>) {
    logRecognitionAction(action, {
      step,
      sending,
      balance: user?.point_categories?.point_behavior,
      pointsSent,
      target,
      behavior,
      coin,
      points,
      message,
      cycle: currentCycle,
    });
  }

  function reset() {
    setStep(STEP.colleague);
    clearSearch();
    setTarget(undefined);
    setBehavior(undefined);
    setCoin(undefined);
    setPoints(undefined);
    setMessageOption(undefined);
    setFreeText(undefined);
    setSending(false);
    setResult(undefined);
  }

  async function searchColleague(typed: string, pickOnlyMatch = true) {
    // Só a busca confirmada (Enter, botão ou a matrícula da rota) vai para o log: a busca ao digitar gravaria um
    // registro a cada pausa (AJ3-40). Esvaziar o campo só limpa a lista e também não vai.
    if (pickOnlyMatch && typed.trim()) logAction({ type: 'search', data: typed });

    const onlyMatch = await findColleagues(typed, pickOnlyMatch);

    if (onlyMatch) setTarget(onlyMatch);
  }

  function selectTarget(person: User) {
    logAction({ type: 'setTarget', data: person._id });
    // A busca do Enter ainda em andamento não troca a pessoa escolhida no clique quando voltar (AJ3-40).
    clearSearch();
    setTarget(person);
  }

  function removeTarget() {
    logAction({ type: 'resetSearch', data: 'Excluir funcionário e voltar para campo de busca' });
    reset();
  }

  function selectBehavior(nextBehavior: BehaviorKey) {
    logAction({ type: 'setBehavior', data: nextBehavior });
    setBehavior(nextBehavior);
  }

  /**
   * Trocar de tipo sempre descarta o valor escolhido: as faixas não se sobrepõem. E em #UAU
   * as mensagens prontas não valem, então a opção pronta escolhida também sai (REC-41).
   */
  function selectCoin(nextCoin: CoinKey) {
    logAction({ type: 'setCoin', data: nextCoin });
    setCoin(nextCoin);
    setPoints(undefined);

    if (!coinAllowsPresets(nextCoin) && messageOption !== FREE_TEXT_OPTION) setMessageOption(undefined);
  }

  /** Editor vazio não é mensagem: sem isso o `<p></p>` de um campo só focado iria para o rascunho. */
  function changeFreeText(content: MessageContent) {
    setFreeText(content.length.fullLength > 0 ? content : undefined);
  }

  function selectPoints(value: number) {
    logAction({ type: 'setPoints', data: value });
    setPoints(value);
  }

  async function clearDraft() {
    await clearSavedDraft();
    reset();
    setRecoveredDraft(false);
  }

  async function submit() {
    logAction({ type: 'handleSubmit' });

    if (sending || !recognition || !messageIsValid) return;

    setSending(true);

    try {
      const serverTime = await getServerTime();

      if (!cycleIsOpenAt(currentCycle, serverTime)) {
        warnNoOpenPhase();
        return;
      }

      const log = await registerRewardBehavior('me', 'reward_behavior', {
        target: recognition.target._id,
        behavior: recognition.behavior,
        coin: recognition.coin,
        points: recognition.points,
        message: recognition.message,
      });
      const sent = log?.attributes?.status === 'OK';

      refreshUser();

      if (sent) clearSavedDraft();

      setResult({
        sent,
        serverMessage: log?.attributes?.systemMessage || (sent ? SENT_MESSAGE : FAILED_MESSAGE),
        recognition,
      });
    } catch (error) {
      refreshUser();
      setResult({ sent: false, serverMessage: FAILED_MESSAGE, recognition });
    } finally {
      setSending(false);
    }
  }

  function goNext() {
    if (!stepIsComplete) return;

    if (step === STEP.review) {
      submit();
      return;
    }

    const nextStep = (step + 1) as Step;

    logAction({ type: step === STEP.message ? 'review' : 'next', location: 'bottom', data: `setStep(${nextStep})` });
    setStep(nextStep);
  }

  function goBack() {
    const previousStep = Math.max(STEP.colleague, step - 1) as Step;

    logAction({ type: 'back', location: 'bottom', data: `setStep(${previousStep})` });
    setStep(previousStep);
  }

  function retry() {
    logAction({ type: 'retry', location: 'submitted' });
    setResult(undefined);
    setStep(STEP.message);
  }

  function close() {
    // `reset: false` é o que o log sempre levou quando o botão do cabeçalho fecha a modal.
    logAction(result ? { type: 'close', location: 'submitted' } : { type: 'close', location: 'header', reset: false });
    notifyDraftChanged();
    navigate('../');
  }

  function dismissRelationship() {
    closeRelationshipQuestion();
    releaseTarget();
  }

  // Fase aberta e dados de quem envia. Sem fase aberta, a modal nem chega a abrir.
  useEffect(() => {
    if (!currentCycle) return;

    let cancelled = false;

    async function checkPhaseAndLoadPlayer() {
      const serverTime = await getServerTime();

      if (cancelled) return;

      if (!cycleIsOpenAt(currentCycle, serverTime)) {
        warnNoOpenPhase();
        navigate('/');
        return;
      }

      const info = await getPlayerInfo(user?._id);

      if (!cancelled) setPlayerInfo(info || {});
    }

    checkPhaseAndLoadPlayer();

    return () => {
      cancelled = true;
    };
  }, [currentCycle, navigate, user?._id]);

  // Rascunho gravado. Com a matrícula na rota, a tela abre na busca por ela, e não no rascunho.
  useEffect(() => {
    if (colleagueIdFromRoute) return;

    let cancelled = false;

    function applyDraft(saved: RecognitionDraft) {
      // O rascunho só guarda o HTML da mensagem: a opção (pronta ou texto livre) é deduzida dele.
      const option = messageOptionFor(saved.message, saved.coin);

      setTarget(saved.target);
      setBehavior(saved.behavior);
      setCoin(saved.coin);
      setPoints(saved.points);
      setMessageOption(option);
      setFreeText(saved.message && option === FREE_TEXT_OPTION ? contentFromHtml(saved.message) : undefined);
      setStep(saved.step);
      setRecoveredDraft(true);
    }

    setLoadingDraft(true);
    readRecognitionDraft().then(saved => {
      if (cancelled) return;
      if (saved) applyDraft(saved);

      setLoadingDraft(false);
    });

    return () => {
      cancelled = true;
    };
  }, [colleagueIdFromRoute]);

  useEffect(() => {
    if (colleagueIdFromRoute) searchColleague(colleagueIdFromRoute);
    // A busca da rota roda uma vez por matrícula; `searchColleague` muda a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colleagueIdFromRoute]);

  /*
   * Liderança Integradora só vale para quem lidera (REC-24). As escolhas ficam quando o colega é
   * trocado, e esta não pode ficar para alguém que não lidera: sem ela não há o que pontuar nem
   * revisar, e a tela volta para a etapa do comportamento.
   */
  useEffect(() => {
    if (!target || target.isLeader || behavior !== LEADER_ONLY_BEHAVIOR) return;

    setBehavior(undefined);

    if (step > STEP.behavior) setStep(STEP.behavior);
  }, [target, behavior, step]);

  useEffect(() => {
    if (target) setRouteTermUsed(true);
  }, [target]);

  useEffect(() => {
    if (step === STEP.coinPoints && sentPointsNotice) setAlert({ text: sentPointsNotice, isNotice: true });
  }, [step, sentPointsNotice]);

  /*
   * Um valor escolhido pode deixar de valer quando o total já enviado chega do servidor ou o
   * saldo é atualizado. Sem valor não há o que revisar nem enviar: volta para a etapa dele.
   *
   * Vale só enquanto a pessoa escolhe. O próprio envio debita o saldo, e o valor que acabou de
   * sair não pode ser desfeito por isso; depois de uma recusa, a regra volta a valer em
   * "Tentar novamente".
   */
  useEffect(() => {
    if (sending || result) return;
    if (!coin || points === undefined) return;

    if (optionState(coin, points, balance, pointsSent) !== 'enabled') {
      setPoints(undefined);

      if (step > STEP.coinPoints) setStep(STEP.coinPoints);
    }
  }, [sending, result, coin, points, balance, pointsSent, step]);

  // Rascunho: grava a cada escolha, da segunda etapa em diante.
  useEffect(() => {
    if (loadingDraft || result || step === STEP.colleague) return;
    if (!target && !behavior && !coin && !messageHtml) return;

    saveDraftSoon({ step, target, behavior, coin, points, message: messageHtml });
  }, [step, target, behavior, coin, points, messageHtml, loadingDraft, result, saveDraftSoon]);

  const view: RecognitionView = sending ? 'sending' : !result ? 'steps' : result.sent ? 'sent' : 'failed';

  return {
    /** Quem renunciou ao reconhecimento ou só consulta não vê a modal. */
    hidden: user?.extra?.recognition === false || !!playerInfo?.visualizador,
    view,
    step,
    /** O que o campo de busca mostra ao abrir. */
    initialSearchTerm: routeTermUsed ? '' : colleagueIdFromRoute,
    search,
    target,
    behavior,
    coin,
    points,
    balance,
    pointsSent,
    messageOption,
    freeText,
    recognition,
    result,
    canAdvance: stepIsComplete,
    canRetry: !!result && !result.sent && !FINAL_REFUSALS.includes(result.serverMessage),
    canClearDraft: recoveredDraft && !loadingDraft && !!(target || behavior || coin || messageHtml),
    alert,
    askingRelationship,
    searchColleague,
    cancelAutoPick,
    selectTarget,
    removeTarget,
    selectBehavior,
    selectCoin,
    selectPoints,
    selectMessageOption: setMessageOption,
    changeFreeText,
    goNext,
    goBack,
    retry,
    close,
    clearDraft,
    dismissAlert: () => setAlert(undefined),
    confirmRelationship: closeRelationshipQuestion,
    dismissRelationship,
  };
}
